/**
 * Copyright 2026 Bluecircle Technologies Pvt Limited. All rights reserved.
 *
 * This software and its source code are the proprietary and confidential
 * intellectual property of Bluecircle Technologies Pvt Limited. Unauthorized
 * copying, reproduction, distribution, modification, public display, or use
 * of this software, in whole or in part, is strictly prohibited without the
 * prior written permission of Bluecircle Technologies Pvt Limited.
 *
 * Portions derived from Grist (Copyright Grist Labs), used under the Apache
 * License, Version 2.0. See the NOTICE file for details.
 */

import { makeT } from "app/client/lib/localization";
import { HomeModel } from "app/client/models/HomeModel";
import { productPill } from "app/client/ui/AppHeader";
import * as css from "app/client/ui/DocMenuCss";
import { buildHomeIntroCards } from "app/client/ui/HomeIntroCards";
import { isNarrowScreenObs, testId, theme, vars } from "app/client/ui2018/cssVars";
import { icon } from "app/client/ui2018/icons";
import { menu, menuCssClass } from "app/client/ui2018/menus";
import { toggleSwitch } from "app/client/ui2018/toggleSwitch";
import { FullUser } from "app/common/LoginSessionAPI";

import { dom, DomContents, styled } from "grainjs";
import { defaultMenuOptions } from "popweasel";

const t = makeT("HomeIntro");

export function buildHomeIntro(homeModel: HomeModel): DomContents {
  const user = homeModel.app.currentValidUser;
  const isAnonym = !user;
  const isPersonal = !homeModel.app.isTeamSite;
  if (isAnonym) {
    return makeAnonIntro(homeModel);
  }
  else if (isPersonal) {
    return makePersonalIntro(homeModel, user);
  }
  else {
    return makeTeamSiteIntro(homeModel);
  }
}

function makeTeamSiteIntro(homeModel: HomeModel) {
  return [
    css.stickyHeader(
      cssHeaderWithPill(
        cssHeader(
          dom.text((use) => {
            if (use(homeModel.onlyShowDocuments)) {
              return t("Welcome to {{- orgName}}", { orgName: homeModel.app.currentOrgName });
            }
            return use(isNarrowScreenObs()) ?
              homeModel.app.currentOrgName :
              t("What goal do you want to achieve today?");
          }),
        ),
        cssPill(productPill(homeModel.app.currentOrg, { large: true })),
        testId("welcome-title"),
      ),
      dom.maybe(homeModel.onlyShowDocuments, () =>
        css.machineControlButton(
          icon("Home"),
          t("Mission Control"),
          dom.on("click", () => homeModel.onlyShowDocuments.set(false)),
          testId("mission-control-button"),
        ),
      ),
      buildPreferencesMenu(homeModel),
    ),
    dom.create(buildHomeIntroCards, { homeModel }),
  ];
}

function makePersonalIntro(homeModel: HomeModel, user: FullUser) {
  return [
    css.stickyHeader(
      cssHeader(
        // this is like using a `<h1>` element, but in our case it's easier to use aria attributes than changing
        // some common `styled` components in order to use a specific h1 here
        { "role": "heading", "aria-level": "1" },
        dom.text((use) => {
          if (use(homeModel.onlyShowDocuments)) {
            return use(isNarrowScreenObs()) ?
              t("Welcome to KNet AI!") :
              t("Welcome to KNet AI, {{- name}}!", { name: sentenceCaseName(user.name) });
          }
          return use(isNarrowScreenObs()) ?
            t("What goal do you want to achieve today?") :
            t("What goal do you want to achieve today, {{- name}}?", { name: sentenceCaseName(user.name) });
        }),
        testId("welcome-title"),
      ),
      dom.maybe(homeModel.onlyShowDocuments, () =>
        css.machineControlButton(
          icon("Home"),
          t("Mission Control"),
          dom.on("click", () => homeModel.onlyShowDocuments.set(false)),
          testId("mission-control-button"),
        ),
      ),
      buildPreferencesMenu(homeModel),
    ),
    dom.create(buildHomeIntroCards, { homeModel }),
  ];
}

// Make the name sentence-cased: capitalize the first character while preserving the rest.
function sentenceCaseName(name: string): string {
  if (!name) { return name; }
  const s = name.trim();
  return s ? s[0].toUpperCase() + s.slice(1) : s;
}

function makeAnonIntro(homeModel: HomeModel) {
  return [
    css.stickyHeader(
      cssHeader(
        dom.text(use => use(homeModel.onlyShowDocuments) ? t("Welcome to KNet AI!") : t("What goal do you want to achieve today?")),
        testId("welcome-title"),
      ),
      dom.maybe(homeModel.onlyShowDocuments, () =>
        css.machineControlButton(
          icon("Home"),
          t("Mission Control"),
          dom.on("click", () => homeModel.onlyShowDocuments.set(false)),
          testId("mission-control-button"),
        ),
      ),
    ),
    dom.create(buildHomeIntroCards, { homeModel }),
  ];
}

function buildPreferencesMenu(homeModel: HomeModel) {
  const { onlyShowDocuments } = homeModel;

  return cssDotsMenu(
    cssDots(icon("Dots")),
    menu(
      () => [
        toggleSwitch(onlyShowDocuments, {
          label: t("Only show documents"),
          args: [
            testId("welcome-menu-only-show-documents"),
          ],
        }),
      ],
      {
        ...defaultMenuOptions,
        menuCssClass: `${menuCssClass} ${cssPreferencesMenu.className}`,
        placement: "bottom-end",
      },
    ),
    testId("welcome-menu"),
  );
}

const cssHeader = styled(css.listHeaderNoWrap, `
  font-size: 20px;
  line-height: 30px;
`);

const cssHeaderWithPill = styled("div", `
  display: flex;
  align-items: center;
  overflow: hidden;
`);

const cssPill = styled("div", `
  flex-shrink: 0;
`);

const cssPreferencesMenu = styled("div", `
  padding: 10px 16px;
`);

const cssDotsMenu = styled("div", `
  display: flex;
  cursor: pointer;
  border-radius: ${vars.controlBorderRadius};

  &:hover, &.weasel-popup-open {
    background-color: ${theme.hover};
  }
`);

const cssDots = styled("div", `
  --icon-color: ${theme.lightText};
  padding: 8px;
`);
